'use client'

import { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'

import { LAYOUT_TEST_IDS } from '@common/common-test-ids.const'

import type { WorkspaceMember } from '@modules/projects/project-members.interface'
import { getProjectMembers } from '@modules/projects/projects'
import { selectCurrentProject } from '@modules/projects/stores/projects.selector'
import { useProjectsStore } from '@modules/projects/stores/projects.store'
import { useWorkspaceSettings } from '@modules/settings/workspace/hooks'
import { selectInviteProjectDialogOpen } from '@stores/layout/layout.selector'
import { useLayoutStore } from '@stores/layout/layout.store'

import { MembersInviteDialog } from '../../MembersInviteDialog'
import { useLayoutInviteHandlers } from '../hooks/useLayoutInviteHandlers'

export function ProjectInviteDialogContainer() {
  const t = useTranslations('core')
  const open = useLayoutStore(selectInviteProjectDialogOpen)
  const setInviteProjectDialogOpen = useLayoutStore((state) => state.setInviteProjectDialogOpen)
  const currentProject = useProjectsStore(selectCurrentProject)
  const { inviteProjectMember, inviteWorkspaceMember } = useWorkspaceSettings()
  const { handleInviteProject } = useLayoutInviteHandlers({ inviteProjectMember, inviteWorkspaceMember })
  const [projectMembersForValidation, setProjectMembersForValidation] = useState<WorkspaceMember[]>([])

  useEffect(() => {
    const projectId = currentProject?.projectId
    if (!open || !projectId) {
      setProjectMembersForValidation([])
      return
    }

    let cancelled = false

    void getProjectMembers(projectId)
      .then((members) => {
        if (!cancelled) {
          setProjectMembersForValidation(members)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProjectMembersForValidation([])
        }
      })

    return () => {
      cancelled = true
    }
  }, [currentProject?.projectId, open])

  return (
    <div data-testid={LAYOUT_TEST_IDS.projectInviteDialogContainer} style={{ display: 'contents' }}>
      <MembersInviteDialog
        open={open}
        onOpenChange={setInviteProjectDialogOpen}
        contextName={currentProject?.name ?? ''}
        onInvite={handleInviteProject}
        membersForValidation={projectMembersForValidation}
        conflictAlreadyMemberMessage={t('inviteToProjectDialog.conflictAlreadyMember')}
        conflictAlreadyInvitedMessage={t('inviteToProjectDialog.conflictAlreadyInvited')}
        title={t('inviteToProjectDialog.title')}
        descriptionPre={t('inviteToProjectDialog.descriptionPre')}
        descriptionMid={t('inviteToProjectDialog.descriptionMid')}
        descriptionPost={t('inviteToProjectDialog.descriptionPost')}
        emailInvalidMessage={t('inviteToProjectDialog.emailInvalid')}
        placeholder={t('inviteToProjectDialog.emailPlaceholder')}
        roleDeveloperLabel={t('inviteToProjectDialog.roleDeveloper')}
        roleAdminLabel={t('inviteToProjectDialog.roleAdmin')}
        cancelLabel={t('inviteToProjectDialog.cancel')}
        confirmLabel={t('inviteToProjectDialog.invite')}
      />
    </div>
  )
}
